// Modules
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, LOCALE_ID, APP_INITIALIZER, provideZoneChangeDetection } from '@angular/core';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { AppRoutingModule } from './app-routing.module';

import { SharedModule } from './shared/shared.module';
import { ConfigurationModule } from './configuration/configuration.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
// Components
import { AppComponent } from './app.component';
import { ReportsComponent } from './reports/reports.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { RootDasboardComponent } from './dashboard/root-dashboard.component';
import { NewRootDasboardComponent } from './dashboard/new-root-dashboard/new-root-dashboard.component';
import { SupportComponent, FeedbackComponent } from './support/index';
import { LogsTimelineComponent } from './logs/logs-timeline/logs-timeline.component';

import { PrestayUtilService } from './layouts/themes/index';

import { GoogleAnalyticsService } from './_services/google-analytics.service';

// i18n
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS, HttpClientJsonpModule } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

// used to create fake backend
import { HtmlHelper } from './_helpers/index';

import { AuthGuard, AuthPrestayGuard } from './_guards/index';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import {
  AlertService,
  AuthenticationService,
  UserService,
  ProductsService,
  VersionCheckService,
  SessionService,
} from './_services/index';

import { NotificationsService } from './notifications/index';

import { LoginComponent } from './login/index';
import { RegisterComponent } from './register/index';

import { FilterDateService } from './shared/filters/date/filter-date-service';

export function HttpLoaderFactory(http: HttpClient) {
  const cacheBuster = new Date().getTime(); // this is used to refresh translation files 
  return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${cacheBuster}`)
}

export function clearLocalStorageFactory(): () => void {
  return () => {
    const storedVersion = localStorage?.getItem('releaseVersion');
    const releaseVersion = environment?.appVersion?.split('.')[1];

    if (storedVersion !== releaseVersion) {
      localStorage.clear();
      localStorage.setItem('releaseVersion', releaseVersion);
    }
  };
}

import { LayoutsComponent } from './layouts/main/layouts.component';

//import { DeviceDetectorModule } from 'ngx-device-detector';

import { DemographyService } from './online/demography/demography.service';
import { SurveyThemeComponent } from './layouts/themes/survey/survey-theme/survey-theme.component';
import { SurveysUtilService } from './features/surveys/surveys-util.service';
import { SupportFieldProductTagComponent } from './support/fields/support-field-product-tag/support-field-product-tag.component';
import { SupportFieldProductComponent } from './support/fields/support-field-product/support-field-product.component';
import { SupportFieldAttachedFilesComponent } from './support/fields/support-field-attached-files/support-field-attached-files.component';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';
import { CustomHttpInterceptorService } from './_services/http_interceptor.service';

import { AirbrakeErrorHandler } from './airbrake-error-handler';
import { environment } from 'src/environments/environment';
import { PrivateDownloadComponent } from 'src/app/prestay/private-download/private-download.component';
import { MhLibComponent } from './mh-lib/mh-lib.component';
import { registerLocaleData } from '@angular/common';
import localeCL from '@angular/common/locales/es-CL';

import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsModule, Store } from '@ngxs/store';
import { SemanticState } from './states/semantic/semantic.state';
import { FollowupOnsiteState } from './states/followup-onsite/followup-onsite.state';
import { PublicConfigState } from './states/public-survey/public-survey.state';
import { DefaultLanguageState } from './states/default-language/default-language.state';
import { FidelityDeskState } from './states/fidelity-desk/fidelity-desk.state';
import { CurrentSurveyState } from './states/survey/survey.state';
import { CustomersState } from './states/customers/customers.state';
import { UsersState } from './states/users/users.state';
import { DateState } from './states/date/date.state';
import { AutoCaseState } from 'src/app/states/auto-case-config/auto-case-config.state';

import { PipesModule } from 'app/pipes/pipes.module';
import { DownloadPageComponent } from './download-page/download-page.component';
import { HubspotSupportComponent } from './support/hubspot-support/hubspot-support.component';
import { OnlineReviewsState } from './states/online-reviews/online-reviews.state';
import { MenusState } from './states/menus/menus.state';
import { SmartRepliesModule } from 'app/new-fs/smart-replies/smart-replies.module';
import { ServiceRequestWidgetComponent } from './dashboard/new-root-dashboard/components/service-request-widget/service-request-widget.component';
import { OtasState } from './states/otas/otas.state';
import { OnlineComponent } from 'app/new-fs/online/online.component';
import { NotificationsCentralComponent } from './notifications-central/notifications-central.component';
import { UsersActionsState } from './states/users-actions/users-actions.state';
import { ContactExecutiveComponent } from './support/contact-executive/contact-executive.component';
import { GestorUsuariosModule } from './new-fs/gestor-usuarios/gestor-usuarios.module';
import { DashboardWidgetsModule } from './inter-chain/dashboard/widgets/dashboard-widgets.module';
import { DashboardWidgetCardComponent } from './dashboard/new-root-dashboard/components/dashboard-widget-card/dashboard-widget-card.component';
import { NpsWidgetContainerComponent } from './dashboard/new-root-dashboard/components/nps-widget-container/nps-widget-container.component';
import { IroWidgetContainerComponent } from './dashboard/new-root-dashboard/components/iro-widget-container/iro-widget-container.component';
import { SemanticIndexWidgetContainerComponent } from './dashboard/new-root-dashboard/components/semantic-index-widget-container/semantic-index-widget-container.component';
import { ResolvedCasesWidgetContainerComponent } from './dashboard/new-root-dashboard/components/resolved-cases-widget-container/resolved-cases-widget-container.component';
import { AutomationRateWidgetContainerComponent } from './dashboard/new-root-dashboard/components/automation-rate-widget-container/automation-rate-widget-container.component';

registerLocaleData(localeCL);

@NgModule({
  declarations: [
    AppComponent,
    ReportsComponent,
    NotFoundComponent,
    SupportComponent,
    FeedbackComponent,
    RootDasboardComponent,
    NewRootDasboardComponent,
    ServiceRequestWidgetComponent,
    LoginComponent,
    RegisterComponent,
    LayoutsComponent,
    SurveyThemeComponent,
    SupportFieldProductTagComponent,
    SupportFieldProductComponent,
    SupportFieldAttachedFilesComponent,
    LogsTimelineComponent,
    PrivateDownloadComponent,
    MhLibComponent,
    DownloadPageComponent,
    HubspotSupportComponent,
    OnlineComponent,
    NotificationsCentralComponent,
    ContactExecutiveComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ConfigurationModule,
    HttpClientModule,
    HttpClientJsonpModule,
    NgxPageScrollCoreModule,
    SharedModule,
    //DeviceDetectorModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'es',
    }),
    NgxsModule.forRoot(
      [
        SemanticState,
        FollowupOnsiteState,
        PublicConfigState,
        DefaultLanguageState,
        FidelityDeskState,
        CurrentSurveyState,
        CustomersState,
        UsersState,
        DateState,
        OnlineReviewsState,
        OtasState,
        MenusState,
        UsersActionsState,
        AutoCaseState,
      ],
      { developmentMode: !environment.production },
    ),
    NgxsLoggerPluginModule.forRoot({ disabled: environment.production }), // revisar dependencia por CSP
    HammerModule,
    PipesModule,
    AppRoutingModule,
    SmartRepliesModule,
    GestorUsuariosModule,
    DashboardWidgetsModule,
    DashboardWidgetCardComponent,
    NpsWidgetContainerComponent,
    IroWidgetContainerComponent,
    SemanticIndexWidgetContainerComponent,
    ResolvedCasesWidgetContainerComponent,
    AutomationRateWidgetContainerComponent,
  ],
  providers: [
    PrestayUtilService,
    AuthPrestayGuard,
    AuthGuard,
    AlertService,
    AuthenticationService,
    UserService,
    ProductsService,
    FilterDateService,
    NotificationsService,
    GoogleAnalyticsService,
    VersionCheckService,
    // providers used to create fake backend
    HtmlHelper,
    SessionService,
    DemographyService,
    SurveysUtilService,
    { provide: HTTP_INTERCEPTORS, useClass: CustomHttpInterceptorService, multi: true },
    // provide error handler only on production
    // this is F****** the logs
    ...(environment.production ? [{ provide: ErrorHandler, useClass: AirbrakeErrorHandler }] : []),
    { provide: LOCALE_ID, useValue: 'es_CL' },
    // CDK 21 monta los overlays en el "top layer" del navegador (Popover API),
    // que se pinta sobre TODO el DOM normal ignorando el z-index. Eso dejaba los
    // tooltips (mhTooltip, colgados de <body>) por detrás de los modales.
    // usePopover: false vuelve al stacking por z-index (comportamiento pre-CDK-21).
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: 'googleTagManagerId', useValue: environment.gtmContainerId },
    {
      provide: APP_INITIALIZER,
      useFactory: clearLocalStorageFactory,
      multi: true,
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(tooltipConfig: NgbTooltipConfig, store: Store) {
    tooltipConfig.container = 'body';

    // NGXS v21 despacha fuera de la zona Angular por defecto (InternalNgxsExecutionStrategy).
    // En Angular 21 esto causa que la vista no se actualice.
    // Parcheamos enter/leave para que todo corra en la zona actual (NoopNgxsExecutionStrategy).
    const strategy = (store as any)._internalExecutionStrategy;
    if (strategy) {
      strategy.enter = (func: () => any) => func();
      strategy.leave = (func: () => any) => func();
    }
  }
}
